/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { createTicket, createKontrolTicket } from "../request/request";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAppSelector } from "@redux/hooks";
import {
  productDataIncident,
  TABS,
} from "settings/constants/constants";
import {
  isAdditionalInfoValid as isAdditionalInfoValidShared,
  isAttachmentsValid as isAttachmentsValidShared,
  extractUniqueCustomers,
  filterServicesByCustomer,
  findProductByLabel,
  findTopicByLabel
} from "../utils/tickets";
import { useLoadServiceItems } from "../hooks/useLoadServiceItems";
import { useWizardTabs } from "../hooks/useWizardTabs";
import { useChangeRequestForm } from "../hooks/useChangeRequestForm";
import { ServiceSelectionTab } from "./IncidentReportModal/ServiceSelectionTab";
import { RequestInfoTab } from "./IncidentReportModal/RequestInfoTab";
import { AttachmentsTab } from "./IncidentReportModal/AttachmentsTab";

interface IncidentReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated?: () => void;
}

export const IncidentReportModal: React.FC<IncidentReportModalProps> = ({
  isOpen,
  onClose,
  onTicketCreated,
}) => {
  const api = useAxiosAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isPartnerView = tenantId === partnerId;
  const [cloud] = useState("CloudGlobal");
  const [region] = useState("us-east-1");
  const [isSaving, setIsSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState<"success" | "error">(
    "success"
  );
  const [alertMessage, setAlertMessage] = useState("");

  // Check if the selected product is "Kontrol Edge"
  const globalProduct = useAppSelector(
    (state) => state.product.selectedProduct
  );
  const isKontrolEdge = globalProduct === "Kontrol Edge";
  // Field specific to Incident Reports: "AffectedServices" (Yes/No) => 10159 / 10160
  const [affectedServices, setAffectedServices] = useState("");
  // For Incident Report, we need to handle topics separately since it uses "Incident Report" type
  const [topics, setTopics] = useState<Array<{ label: string; value: string }>>(
    []
  );
  const [selectedTopicLabel, setSelectedTopicLabel] = useState("");
  const [loadingTopics, setLoadingTopics] = useState(false);
  const form = useChangeRequestForm();

  // Load services if KE
  const { items: serviceItems } = useLoadServiceItems(
    api,
    tenantId,
    isKontrolEdge
  );

  // Extract unique customers from serviceItems
  const uniqueCustomers = React.useMemo(() => {
    return extractUniqueCustomers(serviceItems);
  }, [serviceItems]);

  // Filter services based on selected customer (only in partner view)
  const filteredServiceItems = React.useMemo(() => {
    if (!isPartnerView) {
      // Not in partner view: show all services
      return serviceItems;
    }
    // In partner view: filter by selected customer
    return filterServicesByCustomer(
      serviceItems,
      form.formState.selectedCustomerId
    );
  }, [serviceItems, form.formState.selectedCustomerId, isPartnerView]);

  // Load topics for Incident Report (non-KE only)
  React.useEffect(() => {
    if (isKontrolEdge) return;
    const found = productDataIncident.find((p) => p.label === globalProduct);
    if (!found) return;

    let alive = true;
    setLoadingTopics(true);

    import("../utils/topics").then(({ fetchTopics }) => {
      fetchTopics(api, "Incident Report", found.value)
        .then((d) => {
          if (alive) setTopics(d);
        })
        .catch((err) => console.error("loadTopics error:", err))
        .finally(() => {
          if (alive) setLoadingTopics(false);
        });
    });

    return () => {
      alive = false;
    };
  }, [api, globalProduct, isKontrolEdge]);

  const isLoadingSomething = loadingTopics || isSaving;
  const overlay = useProcessingOverlay(isLoadingSomething);

  // Validation functions
  const isGeneralInfoValid = () => {
    if (isKontrolEdge) {
      // For KE: require services selection, service disruptions, and customer if in partner view
      return (
        (isPartnerView ? !!form.formState.selectedCustomerId : true) &&
        form.formState.selectedServiceIds.length > 0 &&
        affectedServices.trim() !== ""
      );
    }
    return selectedTopicLabel.trim() !== "" && affectedServices.trim() !== "";
  };

  const isAdditionalInfoValid = () => {
    return isAdditionalInfoValidShared(isKontrolEdge, {
      cloud,
      region,
      title: form.formState.title,
      description: form.formState.description,
      priority: form.formState.priority,
    });
  };

  const isAttachmentsValid = () => {
    return isAttachmentsValidShared(
      isKontrolEdge,
      form.formState.sendResponse,
      form.formState.contactName,
      form.formState.contactEmail
    );
  };

  // Tabs via hook
  const {
    activeTab,
    go: goTab,
    canActivate,
  } = useWizardTabs("serviceSelection", {
    serviceSelection: () => true,
    requestInfo: () => isGeneralInfoValid(),
    attachments: () => isGeneralInfoValid() && isAdditionalInfoValid(),
  });

  const handleChangeTab = (id: string) => goTab(id);

    // Handlers for Incident Report specific fields
    const handleServicesDropdownChange = (label: string) => {
      const valueMap: Record<string, string> = {
        "Yes": "10159",
        "No": "10160",
      };
      setAffectedServices(valueMap[label] || "");
    };

  const getServicesLabel = () => {
    const labelMap: Record<string, string> = {
      "10159": "Yes",
      "10160": "No",
    };
    return labelMap[affectedServices] || "";
  };

  // Priority options - Urgent only available if affectedServices !== "10160" (No)
  const getPriorityOptions = () => {
    const baseOptions = ["Low", "Medium", "High"];
    if (affectedServices !== "10160") {
      return [...baseOptions, "Urgent"];
    }
    return baseOptions;
  };

  const handleSelectTopic = (topicLabel: string) => {
    setSelectedTopicLabel(topicLabel);
  };

  const handleCustomerSelect = (customerNameDisplay: string) => {
    form.handleCustomerSelect(customerNameDisplay, uniqueCustomers);
  };

  const handleServiceSelect = (selectedLabels: string[]) => {
    form.handleServiceSelect(selectedLabels, filteredServiceItems);
  };

  // Reset form state
  const resetFormState = () => {
    setTopics([]);
    setSelectedTopicLabel("");
    setAffectedServices("");
    form.resetForm();
    handleChangeTab("serviceSelection");
  };

  const handleCloseModal = () => {
    resetFormState();
    onClose();
  };

  // Save ticket
  const handleSave = async () => {
    if (!isAttachmentsValid()) return;
    setIsSaving(true);
    try {
      const foundProduct = findProductByLabel(
        productDataIncident,
        globalProduct || ""
      );
      const productValue = foundProduct ? foundProduct.value : "";
      const foundTopic = findTopicByLabel(topics, selectedTopicLabel);
      const topicValue = foundTopic ? foundTopic.value : "";

      // Priority mapping for Incident Report: Low(4), Medium(3), High(2), Urgent(10000)
      const priorityMap: Record<string, number> = {
        Low: 4,
        Medium: 3,
        High: 2,
        Urgent: 10000,
      };
      const priorityNumber =
        priorityMap[form.formState.priority] ||
        parseInt(form.formState.priority, 10);

      if (isKontrolEdge) {
        // Validation: require customer only in partner view
        if (isPartnerView && !form.formState.selectedCustomerId) {
          setAlertVariant("error");
          setAlertMessage(
            "Please select a customer and at least one service before creating the ticket."
          );
          setAlertOpen(true);
          setIsSaving(false);
          return;
        }
        if (form.formState.selectedServiceIds.length === 0) {
          setAlertVariant("error");
          setAlertMessage(
            "Please select at least one service before creating the ticket."
          );
          setAlertOpen(true);
          setIsSaving(false);
          return;
        }

        const kontrolPayload: any = {
          requesttype: "INCIDENT_REPORT",
          summary: form.formState.title,
          priority: priorityNumber,
          description: form.formState.description,
          subscriptionID:
            form.formState.selectedSubscriptionIds.length > 0
              ? form.formState.selectedSubscriptionIds
              : [],
          serviceType:
            form.formState.selectedServiceTypes.length > 0
              ? form.formState.selectedServiceTypes
              : [],
          files: form.formState.attachmentFiles
            ? Array.from(form.formState.attachmentFiles)
            : [],
        };
        // Always include end_customer
        // Partner view: use selected customer's ID
        // Customer view: use tenantId (current customer's ID)
        kontrolPayload.end_customer = isPartnerView 
          ? (form.formState.selectedCustomerId as number) 
          : tenantId;
        //console.log(kontrolPayload, 'kontrol payload')
        const response = await createKontrolTicket(api, kontrolPayload);
        if (onTicketCreated) {
          onTicketCreated();
        }
        //console.log("Kontrol ticket created =>", response.data);
      } else {
        const finalPayload: any = {
          Tipo: "Incident Report",
          product: productValue,
          cloud,
          region,
          TipoSolicitud: topicValue,
          AffectedServices: affectedServices,
          Resumen: form.formState.title,
          Prioridad: priorityNumber,
          Descripcion: form.formState.description,
          files: form.formState.attachmentFiles
            ? Array.from(form.formState.attachmentFiles)
            : [],
        };
        if (form.formState.sendResponse) {
          finalPayload.Nombre_de_contacto = form.formState.contactName;
          finalPayload.Correo = form.formState.contactEmail;
          finalPayload.phone_number = `${
            form.formState.selectedCountryCode
          }${form.formState.contactPhoneNumber.replace(/\s+/g, "")}`;
        }
        //console.log(finalPayload, 'finalPayload')
        await createTicket(api, finalPayload);
      }
      setAlertVariant("success");
      setAlertMessage("¡Incident ticket created successfully!");
      setAlertOpen(true);
    } catch (err) {
      console.error("Error creating incident =>", err);
      setAlertVariant("error");
      setAlertMessage(
        "A problem occurred while creating the incident, please try again."
      );
      setAlertOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      closeOnBackdropClick={false}
      size="lg"
      title={
        <div className="flex items-center gap-2">
          <span className="text-semantic-text-primary">Incident Report</span>
          <GenericTooltip
            text="Use this section to tell us about INCIDENTS or ISSUES of greater involvement."
            position="top"
          />
        </div>
      }
    >
      <div className="border-b border-gray-200 mb-4">
        <nav className="flex space-x-4" aria-label="Tabs">
          {TABS.map((tab) => {
            const disabled = !canActivate(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => handleChangeTab(tab.id)}
                disabled={disabled}
                className={`
                  px-3 py-2 text-sm font-medium focus:outline-none
                  ${
                    activeTab === tab.id
                      ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary"
                      : disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-gray-500 hover:text-gray-700"
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "serviceSelection" && (
          <ServiceSelectionTab
            isKontrolEdge={isKontrolEdge}
            isPartnerView={isPartnerView}
            form={form}
            uniqueCustomers={uniqueCustomers}
            filteredServiceItems={filteredServiceItems}
            topics={topics}
            selectedTopicLabel={selectedTopicLabel}
            onCustomerSelect={handleCustomerSelect}
            onServiceSelect={handleServiceSelect}
            onSelectTopic={handleSelectTopic}
            onServicesDropdownChange={handleServicesDropdownChange}
            getServicesLabel={getServicesLabel}
            isGeneralInfoValid={isGeneralInfoValid}
            onNext={() => handleChangeTab("requestInfo")}
          />
      )}

      {activeTab === "requestInfo" && (
        <RequestInfoTab
          isKontrolEdge={isKontrolEdge}
          form={form}
          cloud={cloud}
          region={region}
          affectedServices={affectedServices}
          getPriorityOptions={getPriorityOptions}
          isAdditionalInfoValid={isAdditionalInfoValid}
          onBack={() => handleChangeTab("serviceSelection")}
          onNext={() => handleChangeTab("attachments")}
        />
      )}

      {activeTab === "attachments" && (
        <AttachmentsTab
          isKontrolEdge={isKontrolEdge}
          form={form}
          isSaving={isSaving}
          isAttachmentsValid={isAttachmentsValid}
          onBack={() => handleChangeTab("requestInfo")}
          onSave={handleSave}
        />
      )}

      {overlay}

      <GenericAlert
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={() => {
          setAlertOpen(false);
          if (alertVariant === "success") {
            handleCloseModal();
          }
        }}
        variant={alertVariant}
        title={alertVariant === "success" ? "Incident Report Created" : "Error"}
        description={alertMessage}
        confirmLabel="OK"
      />
    </GenericModal>
  );
};

export default IncidentReportModal;
